import { Component, createRef } from 'react';
import classNames from 'classnames/bind';
import autoBind from 'react-autobind';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import sortBy from 'lodash-es/sortBy';
import uniqBy from 'lodash-es/uniqBy';
import debounce from 'lodash-es/debounce';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';

import { MAX_SELECTED_DOMAINS } from '../../../../data/filters/filterConstants';
import { sourcesTabs } from '../../../../data/filters/storyFilters';

import { SourcesSearchScreen, DomainsSelectionInfo, SourcesInfo } from '../SourcesHelperComponents';
import BluePreloader from '../../../GeneralComponents/BluePreloader';
import Preloader from '../../../Preloader';

import { getDomains, cancelPendingDomainRequests } from '../../../../services/dbServices/storyFiltersDbServices';

import * as storyFiltersActions from '../../../../actions/storyFilters.action';

import withComponentName from '../../../../decorators/withComponentName';

import Styles from '../../story-filters-styles.module.scss';

const cx = classNames.bind(Styles);

class SourcesSelection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      domainsCategoryType: 'all',
      searchMode: false,
      domains: [],
      whitelist: [],
      blacklist: [],
      hasMore: true,
      isLoading: false,
      domainsQuery: '',
      page: 1,
      majorDomainsQty: '',
      premiumDomainsQty: '',
      othersDomainsQty: '',
    };
    autoBind(this);
    this.scrollBlock = createRef();
    this.domainsInput = createRef();
    this.listEl = createRef();
    this.searchDomains = debounce(this.searchDomains, 200);
  }

  componentDidMount() {
    const { storyFilters } = this.props;

    this.setState({
      domainFilteringMode: storyFilters.domains_filtering_mode,
      whitelist: storyFilters.whitelisted_domain_ids,
      blacklist: storyFilters.blacklisted_domain_ids,
      isLoading: true,
    });

    this.loadAllDomains();
  }

  componentDidUpdate(prevProps) {
    const { sourceCategory, domainFilteringMode } = this.props;
    if (sourceCategory !== prevProps.sourceCategory) {
      this.setState({ domainsQuery: '', page: 1 }, () => this.setDomainCategory('all'));
    }
    if (prevProps.domainFilteringMode !== domainFilteringMode) {
      this.setState({ domainFilteringMode });
    }
  }

  onScrollDomainsLoad = debounce(() => {
    const {
      page, hasMore,
    } = this.state;

    if (!this.scrollBlock.current) {
      return;
    }

    const bottom = (
      this.scrollBlock.current.scrollHeight - this.scrollBlock.current.scrollTop
      === this.scrollBlock.current.clientHeight
    );

    if (bottom && hasMore) {
      this.setState({ page: page + 1, isLoading: true }, () => {
        const {
          domainsCategoryType,
          domainsQuery,
        } = this.state;
        this.loadNewDomains(domainsCategoryType, domainsQuery, true);
      });
    }
  }, 500);

  getSourcesQuantity(tabName) {
    const {
      totalDomainsQty, majorDomainsQty, premiumDomainsQty, othersDomainsQty,
    } = this.state;

    switch (tabName) {
      case 'all':
        return `${totalDomainsQty}+`;
      case 'major':
        return majorDomainsQty;
      case 'premium':
        return premiumDomainsQty;
      case 'others':
        return othersDomainsQty;
      default:
        return `${totalDomainsQty}+`;
    }
  }

  setDomainCategory(tabKey) {
    const { domainsQuery, searchMode } = this.state;

    this.setState({ domainsCategoryType: tabKey, page: 1, isLoading: true }, () => {
      if (tabKey !== 'all' && !searchMode) this.scrollBlock.current.scrollTo(0, 0);
      this.loadNewDomains(tabKey, domainsQuery);
    });
  }

  async loadAllDomains(page = 1, query = '', resultsNumber, scrollLoad) {
    let { domains } = this.state;
    const { searchMode } = this.state;
    const { domainFilteringMode, storyFilters } = this.props;

    const params = {
      per_page: searchMode ? resultsNumber : 20,
      page: scrollLoad ? page : 1,
      query,
    };

    const idsKey = domainFilteringMode === 'blacklist' ? 'blacklisted_domain_ids' : 'whitelisted_domain_ids';
    cancelPendingDomainRequests();

    let selectedDomains = [];
    if (
      (domainFilteringMode === 'blacklist' || domainFilteringMode === 'whitelist')
      && (storyFilters[idsKey] || []).length
      && page === 1
      && !query
    ) {
      const selectedParams = {
        domain_ids: (storyFilters[idsKey] || []).join(','),
        per_page: 100,
      };
      ({ data: selectedDomains } = await getDomains(selectedParams));
    }
    /*
      Major Tab: is_major = true
      Premium Tab: is_premiun = true
      Other Tan: is_major = false, is_premium = false
    */

    const { data: allDomains, headers: { total: totalAll } } = await getDomains(params);

    // if there query, we remove all domains from state
    if (query.length) domains = [];

    this.setState({
      isLoading: false,
      domains: uniqBy(sortBy([...(scrollLoad ? domains : []), ...selectedDomains, ...allDomains], 'name'), 'id'),
      hasMore: allDomains.length === 20,
      totalDomainsQty: totalAll,
    });
  }

  async loadNewDomains(domainsType = 'all', query = '', scrollLoad) {
    const { page, domains } = this.state;

    if (domainsType === 'all') return this.loadAllDomains(page, query, 20, scrollLoad);

    const { domainFilteringMode, storyFilters } = this.props;
    const idsKey = domainFilteringMode === 'blacklist' ? 'blacklisted_domain_ids' : 'whitelisted_domain_ids';

    const typeParams = {};
    if (domainsType === 'major') {
      typeParams.major = true;
    } else if (domainsType === 'premium') {
      typeParams.is_premium = true;
    } else {
      typeParams.major = false;
      typeParams.is_premium = false;
    }

    const params = {
      ...typeParams,
      query,
      per_page: 20,
      page,
    };

    let selectedDomains = [];
    if (
      (domainFilteringMode === 'blacklist' || domainFilteringMode === 'whitelist')
      && (storyFilters[idsKey] || []).length
      && page === 1
      && !query
    ) {
      const selectedParams = {
        ...typeParams,
        domain_ids: (storyFilters[idsKey] || []).join(','),
        per_page: 100,
      };
      ({ data: selectedDomains } = await getDomains(selectedParams));
    }

    const { data, headers: { total: totalQty } } = await getDomains(params);
    this.setState({
      domains: uniqBy(sortBy([...(scrollLoad ? domains : []), ...selectedDomains, ...data], 'name'), 'id'),
      hasMore: data.length === 20,
      isLoading: false,
      [`${domainsType}DomainsQty`]: totalQty,
    });
  }

  searchDomains(domainsType, { target }) {
    if (!target.value.length) {
      return this.setState({
        domainsQuery: '',
        searchMode: false,
        isLoading: true,
      }, () => this.loadNewDomains(domainsType));
    }

    this.setState({
      domainsQuery: target.value,
      searchMode: true,
      isLoading: true,
    }, () => {
      this.loadNewDomains(domainsType, target.value);
    });
  }

  unselectAllDomains(sourceCategory) {
    const { setSelectedDomains } = this.props;

    this.setState({
      [sourceCategory]: [],
      [`${sourceCategory}Quantity`]: 0,
    }, () => {
      const { [sourceCategory]: sourceCategoryValue } = this.state;
      setSelectedDomains({ [sourceCategory]: sourceCategoryValue });
    });
  }

  selectDomain(id, sourceCategory) {
    const { setSelectedDomains } = this.props;
    const {
      [sourceCategory]: selectedDomainsCategory, [`${sourceCategory}Quantity`]: limit,
      domainFilteringMode, searchMode, domainsCategoryType,
    } = this.state;

    // if all category selected do not make any selection actions
    if (domainFilteringMode === 'all') return;

    // unselect active domain
    if (selectedDomainsCategory.includes(id)) {
      return this.setState({
        [sourceCategory]: [...selectedDomainsCategory.filter((item) => item !== id)],
        [`${sourceCategory}Quantity`]: selectedDomainsCategory.filter((item) => item !== id).length,

      }, () => {
        const { [sourceCategory]: sourceCategoryValue } = this.state;
        setSelectedDomains({ [sourceCategory]: sourceCategoryValue });
      });
    }

    if (limit === 100) return;

    let curScrollPos; let oldScroll; let
      newScroll;
    if (
      (searchMode && domainsCategoryType !== 'all')
      || !searchMode
    ) {
      curScrollPos = this.scrollBlock.current.scrollTop;
      oldScroll = this.scrollBlock.current.scrollHeight - this.scrollBlock.current.clientHeight;
      newScroll = this.scrollBlock.current.scrollHeight - this.scrollBlock.current.clientHeight;
    }

    this.setState({
      [sourceCategory]: [id, ...selectedDomainsCategory],
      [`${sourceCategory}Quantity`]: [...selectedDomainsCategory, id].length,

    }, () => {
      const { [sourceCategory]: sourceCategoryValue } = this.state;
      setSelectedDomains({ [sourceCategory]: sourceCategoryValue });

      if (
        (searchMode && domainsCategoryType !== 'all')
        || !searchMode
      ) {
        this.scrollBlock.current.scrollTo(0, curScrollPos + (newScroll - oldScroll));
      }
    });
  }

  filterSelectedDomains(id, domainsCategory) {
    const { [domainsCategory]: selectedDomains } = this.state;

    // do not filter domains selection if category is 'all'
    if (domainsCategory === 'all') return false;
    return selectedDomains.includes(id);
  }

  clearDomainsSearch() {
    this.setState({
      domainsQuery: '',
      domainsCategoryType: 'all',
      searchMode: false,
    }, () => {
      this.domainsInput.current.value = '';
      this.domainsInput.current.blur();

      // load all tab domains
      this.loadNewDomains('all');
    });
  }

  render() {
    const {
      sourceCategory, domainsSelectionFeature, domainFilteringMode,
    } = this.props;
    const {
      domainsCategoryType, domains, whitelist, blacklist, domainsQuery, searchMode, isLoading,
      [`${sourceCategory}Quantity`]: limit, [sourceCategory]: selectedCategoryDomains = [],
    } = this.state;

    const domainsListClass = cx({
      'domains-list': true,
      'select-domains-active': domainsSelectionFeature && sourceCategory !== 'all',
      'domains-search-mode': searchMode,
      'premium-domains-list': domainsCategoryType === 'premium',
      hidden: isLoading,
    });

    // filter domains list to show selected first
    const filteredBySelectionDomains = [
      ...domains.filter((val) => selectedCategoryDomains.includes(val.id)),
      ...domains.filter((val) => !selectedCategoryDomains.includes(val.id)),
    ];

    return (
      <div className={Styles['sources-selection']}>
        <DomainsSelectionInfo sourceCategory={sourceCategory} />
        {sourceCategory === 'all' && (
          <BluePreloader
            active={isLoading}
            className={cx('all-sources-loader')}
          />
        )}
        <div className={cx('sources-list-wrapper', { 'non-premium-user-sources': !domainsSelectionFeature })}>
          {domainsSelectionFeature && sourceCategory !== 'all' && (
            <>
              <div className={Styles['search-domains']}>
                <FontAwesomeIcon className={cx('search-domains-icon')} icon={faSearch} />
                <input
                  type="text"
                  placeholder="Type to search"
                  defaultValue={domainsQuery}
                  ref={this.domainsInput}
                  onChange={(e) => {
                    e.persist();
                    this.searchDomains(domainsCategoryType, e);
                  }}
                />
                {domainsQuery.length > 0 && (
                  <FontAwesomeIcon
                    icon={faXmark}
                    className={cx('clear-domains-input')}
                    onClick={this.clearDomainsSearch}
                  />
                )}
              </div>

              <BluePreloader active={isLoading} />

              <ul className={Styles['sources-tabs-list']}>
                {sourcesTabs.map(({ key, id, title }) => (
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                  <li
                    key={id}
                    onClick={() => this.setDomainCategory(key)}
                    className={cx('sources-tab-item', { active: key === domainsCategoryType })}
                  >
                    {title}
                  </li>
                ))}
              </ul>
            </>
          )}

          {domainsCategoryType === 'premium' && filteredBySelectionDomains.length > 0 && (
            <p className={Styles['premium-source-info']}>
              All Premium sources in one list. Some sources are both Premium and free.
              Use the Content Source Filter to toggle Premium-only content.
            </p>
          )}
          {/* do not display source info if user typed something in domains search inout */}
          {!searchMode && (
            <>
              <SourcesInfo
                unselectAllDomains={this.unselectAllDomains}
                getSourcesQuantity={this.getSourcesQuantity}
                domainsCategoryType={domainsCategoryType}
                selectedDomains={sourceCategory === 'whitelist' ? whitelist?.length : blacklist?.length}
                sourceCategory={sourceCategory}
                limitReached={limit === MAX_SELECTED_DOMAINS}
                selectedDomainCategory={domainsCategoryType}
                isLoading={isLoading}
              />
              <Preloader
                loading={isLoading}
                static
                centered
              />
            </>
          )}
          {/* display search screen if user typed something in domains search inout */}
          {
            searchMode
              && domainsCategoryType === 'all' && domainFilteringMode !== 'all'
              ? (
                <SourcesSearchScreen
                  domainsCategoryType={domainsCategoryType}
                  domains={domains}
                  filterSelectedDomains={this.filterSelectedDomains}
                  selectDomain={this.selectDomain}
                  sourceCategory={sourceCategory}
                  setDomainCategory={this.setDomainCategory}
                  limitReached={limit === MAX_SELECTED_DOMAINS}
                  selectedDomainCategory={domainsCategoryType}
                />
              ) : (
                <div className={Styles['domains-list-wrapper']}>
                  {limit === MAX_SELECTED_DOMAINS && searchMode && (
                    <p className={Styles['domains-list-limit']}>100 / 100 selected. You’ve reached the limit</p>
                  )}
                  {!filteredBySelectionDomains.length && !isLoading && (
                    <p className={Styles['no-domains-found']}>No results found</p>
                  )}
                  {Boolean(filteredBySelectionDomains.length) && (
                    <ul
                      className={domainsListClass}
                      ref={this.scrollBlock}
                      onScroll={this.onScrollDomainsLoad}
                    >
                      {filteredBySelectionDomains.map(({ id, name }) => (
                        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                        <li
                          key={`${name}${id}`}
                          className={cx('parent_checkbox', {
                            active: this.filterSelectedDomains(id, sourceCategory),
                            disabled: limit === MAX_SELECTED_DOMAINS,
                          })}
                          ref={this.listEl}
                          onClick={() => this.selectDomain(id, sourceCategory)}
                        >
                          <div className={cx('checkbox_wrapper')}>
                            <span className={cx('checkbox_box')}>
                              {!!this.filterSelectedDomains(id, sourceCategory) && (
                                <FontAwesomeIcon icon={faCheck} />
                              )}
                            </span>
                            {name}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )
          }
        </div>
      </div>
    );
  }
}

function mapToStateProps(state) {
  const { permissions, permissionsInitialized } = state.subscriptions;

  return {
    permissionsInitialized,
    permissions,
    storyFilters: state.storyFilters,
    token: state.watchlistReducer.userToken,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({
      ...storyFiltersActions,
    }, dispatch),
  };
}

export default withComponentName(connect(mapToStateProps, mapDispatchToProps)(SourcesSelection));
